/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import BaseButton from "components/common/Button/Base";
import { StoreType, SubType } from "models/business.model";
import ReportDialog from "pages/Store/Dialog/Report";
import { useState } from "react";
import { PALETTE } from "styles/base/variables";

interface ReportButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  storeType: StoreType | undefined;
  subType?: SubType;
  bizId: string | false | undefined;
  selectedDataIds: string[]; // reportType이 submit일 때만 사용
  variant?: "text" | "contained" | "outlined";
  position?: "bottom" | "right" | "left";
  children?: React.ReactNode;
  successCallback?: () => void;
}

const posBottom = `
  border-top: none;
  top: 100%;
  left: 0;
`;

const posLeft = ` 
  padding-top: 0;
  padding-bottom: 0;
  border-right: none;
  top: 0;
  right: 100%;
`;

const posRight = `
  border-left: none;
  top: 0;
  left: 100%;
`;

function ReportButton({
  storeType,
  subType,
  bizId,
  selectedDataIds,
  variant: _variant = "contained",
  position: _position = "bottom",
  children,
  successCallback,
  ...props
}: ReportButtonProps) {
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState<boolean>(false);
  const [activeReportType, setActiveReportType] = useState<
    "submit" | "sync"
  >("submit");

  const openReportDialog = (type: "submit" | "sync") => {
    setActiveReportType(type);
    setIsReportDialogOpen(true);
    setIsReportDropdownOpen(false);
  };

  const closeReportDialog = () => {
    setIsReportDialogOpen(false);
  };

  const handlePosition = (pos: "bottom" | "left" | "right") => {
    switch (pos) {
      case "bottom":
        return posBottom;
      case "left":
        return posLeft;
      case "right":
        return posRight;
    }
  };

  return (
    <div
      css={css`
        position: relative;
      `}
      onMouseEnter={() =>
        !props.disabled && bizId && setIsReportDropdownOpen(true)
      }
      onMouseLeave={() => setIsReportDropdownOpen(false)}
    >
      <BaseButton
        variant={_variant}
        color={PALETTE.primary}
        disabled={!bizId}
        css={css`
          cursor: default !important;
        `}
        {...props}
      >
        {children || "세무사랑"}
      </BaseButton>
      <div
        css={css`
          min-width: 72px;
          padding: 4px 8px;
          display: ${isReportDropdownOpen ? "flex" : "none"};
          border: 1px solid ${PALETTE.border};
          border-radius: 4px;
          flex-direction: column;
          position: absolute;
          ${handlePosition(_position)}
          background-color: ${PALETTE.light};
          z-index: 100;
        `}
      >
        <BaseButton
          variant="text"
          color={PALETTE.primary}
          onClick={() => openReportDialog("submit")}
          css={css`
            padding: 8px;
            word-break: keep-all;
          `}
        >
          전송
        </BaseButton>
        <BaseButton
          variant="text"
          color={PALETTE.primary}
          onClick={() => openReportDialog("sync")}
          css={css`
            padding: 8px;
            word-break: keep-all;
          `}
        >
          동기화
        </BaseButton>
      </div>
      {Boolean(bizId) && (
        <ReportDialog
          isOpen={isReportDialogOpen}
          onClose={closeReportDialog}
          storeType={storeType}
          subType={subType}
          reportType={activeReportType}
          bizId={bizId as string}
          selectedDataIds={selectedDataIds}
          successCallback={successCallback}
        />
      )}
    </div>
  );
}

export default ReportButton;
